import React, { useState, useEffect } from 'react';
import { bulkAssignShifts, checkCoverage, CoverageReport } from '../../services/rosterService';
import { format } from 'date-fns';
import apiClient from '../../services/api';
import { DatePicker } from '../ui/DatePicker';

interface BulkAssignmentModalProps {
    onClose: () => void;
    onSuccess: () => void;
    startDate: Date;
    versionId?: string;
}

interface User {
    id: string;
    name: string;
    email: string;
    role: {
        name: string;
    };
}

const BulkAssignmentModal: React.FC<BulkAssignmentModalProps> = ({
    onClose,
    onSuccess,
    startDate,
    versionId,
}) => {
    const [date, setDate] = useState(format(startDate, 'yyyy-MM-dd'));
    const [startTime, setStartTime] = useState('07:30');
    const [endTime, setEndTime] = useState('18:30');
    const [shiftType, setShiftType] = useState('CLEARER');
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [coverage, setCoverage] = useState<CoverageReport | null>(null);
    const [error, setError] = useState('');

    useEffect(() => {
        const loadUsers = async () => {
            try {
                const response = await apiClient.get('/users', {
                    params: { limit: 100 },
                });
                setUsers(response.data.users || []);
            } catch (error) {
                console.error('Failed to load users', error);
            } finally {
                setLoadingUsers(false);
            }
        };
        loadUsers();
    }, []);

    useEffect(() => {
        const loadCoverage = async () => {
            try {
                const report = await checkCoverage(new Date(date));
                setCoverage(report);
            } catch (error) {
                console.error('Failed to load coverage', error);
            }
        };
        if (date) {
            loadCoverage();
        }
    }, [date, shiftType, selectedUserIds]);

    const toggleUser = (userId: string) => {
        setSelectedUserIds((prev) =>
            prev.includes(userId)
                ? prev.filter((id) => id !== userId)
                : [...prev, userId]
        );
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (selectedUserIds.length === 0) {
            setError('Please select at least one user');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const startDateTime = new Date(`${date}T${startTime}:00`);
            const endDateTime = new Date(`${date}T${endTime}:00`);

            await bulkAssignShifts({
                userIds: selectedUserIds,
                startTime: startDateTime.toISOString(),
                endTime: endDateTime.toISOString(),
                type: shiftType,
                versionId,
            });

            onSuccess();
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || 'Failed to assign shifts');
        } finally {
            setLoading(false);
        }
    };

    const filteredUsers = users.filter((user) => {
        // Filter by role based on shift type
        const role = user.role.name.toUpperCase();
        if (shiftType === 'CLEARER') {
            return role === 'CLEARER';
        }
        if (shiftType === 'CONTROL') {
            return role === 'CONTROLLER';
        }
        return true;
    });

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <h3 className="text-lg font-bold mb-4">Bulk Assign Shifts</h3>

                {error && <div className="mb-4 text-red-600 text-sm">{error}</div>}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <DatePicker
                                label="Date"
                                value={date}
                                onChange={setDate}
                                placeholder="Pick date"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2">Shift Type</label>
                            <select
                                value={shiftType}
                                onChange={(e) => {
                                    setShiftType(e.target.value);
                                    setSelectedUserIds([]); // Clear selection when type changes
                                }}
                                className="w-full border rounded p-2"
                            >
                                <option value="LGW">LGW</option>
                                <option value="CONTROL">Control</option>
                                <option value="OFFICE">Office</option>
                                <option value="WAREHOUSE">Warehouse</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-2">Start Time</label>
                            <input
                                type="time"
                                value={startTime}
                                onChange={(e) => setStartTime(e.target.value)}
                                className="w-full border rounded p-2"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2">End Time</label>
                            <input
                                type="time"
                                value={endTime}
                                onChange={(e) => setEndTime(e.target.value)}
                                className="w-full border rounded p-2"
                                required
                            />
                        </div>
                    </div>

                    {coverage && (
                        <div className={`p-3 rounded ${coverage.isSufficient ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                            <h4 className="font-semibold mb-2">Coverage Preview</h4>
                            <div className="text-sm">
                                <div>Current: {coverage.coverage[shiftType] || 0}</div>
                                <div>Required: {shiftType === 'CLEARER' ? 2 : 1}</div>
                                {!coverage.isSufficient && (
                                    <div className="text-red-600 mt-1">
                                        {coverage.alerts.map((alert, idx) => (
                                            <div key={idx}>{alert}</div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium mb-2">
                            Select Staff ({selectedUserIds.length} selected)
                        </label>
                        {loadingUsers ? (
                            <div className="text-center py-4">Loading users...</div>
                        ) : (
                            <div className="border rounded p-3 max-h-60 overflow-y-auto">
                                {filteredUsers.length === 0 ? (
                                    <div className="text-gray-500 text-center py-4">
                                        No users available for this shift type
                                    </div>
                                ) : (
                                    filteredUsers.map((user) => (
                                        <label
                                            key={user.id}
                                            className="flex items-center p-2 hover:bg-gray-50 cursor-pointer"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedUserIds.includes(user.id)}
                                                onChange={() => toggleUser(user.id)}
                                                className="mr-2"
                                            />
                                            <span>{user.name} ({user.role.name})</span>
                                        </label>
                                    ))
                                )}
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-2 mt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                            disabled={loading || selectedUserIds.length === 0}
                        >
                            {loading ? 'Assigning...' : `Assign to ${selectedUserIds.length} Staff`}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default BulkAssignmentModal;

